import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactCrop, {
  convertToPixelCrop,
  type Crop,
  type PixelCrop,
} from "react-image-crop";
import { FiCheck, FiEdit2, FiEye, FiPlus, FiTrash2, FiUpload, FiX } from "react-icons/fi";

import { getSignaturePreviewUrl } from "../../../API/signatureAPI";
import {
  createUserSignature,
  deleteUserSignature,
  getUserSignaturePreviewUrl,
  listUserSignatures,
  processUserSignaturePreview,
  renameUserSignature,
  ProcessedSignaturePreview,
  UserSignatureInfo,
  UserSignatureReference,
} from "../../../API/userSignatureAPI";
import { QuestionItem } from "../../../types/form";
import { cropImageToFile } from "../../../utils/cropImage";

import "react-image-crop/dist/ReactCrop.css";

interface SignatureFieldRendererProps {
  question: QuestionItem;
  inputName: string;
  currentValue: unknown;
  applicationId: number;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  readOnly?: boolean;
}

function createDefaultCrop(_mediaWidth: number, _mediaHeight: number): Crop {
  return {
    unit: "%",
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  };
}

const checkerboardStyle: React.CSSProperties = {
  backgroundColor: "#fff",
  backgroundImage:
    "linear-gradient(45deg,#edf0f4 25%,transparent 25%),linear-gradient(-45deg,#edf0f4 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#edf0f4 75%),linear-gradient(-45deg,transparent 75%,#edf0f4 75%)",
  backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
  backgroundSize: "16px 16px",
};

function isUserSignatureReference(value: unknown): value is UserSignatureReference {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return (
    record.type === "user_signature" &&
    Number.isInteger(Number(record.id)) &&
    Number(record.id) > 0
  );
}

function getLegacySignatureId(value: unknown): number | null {
  if (isUserSignatureReference(value) || value === "" || value == null) return null;
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const candidate = error as {
      message?: string;
      response?: { data?: { message?: string; error?: { message?: string } } };
    };
    return (
      candidate.response?.data?.message ||
      candidate.response?.data?.error?.message ||
      candidate.message ||
      fallback
    );
  }
  return fallback;
}

const SignatureFieldRenderer: React.FC<SignatureFieldRendererProps> = ({
  question,
  inputName,
  currentValue,
  applicationId,
  setFormData,
  readOnly = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropImageRef = useRef<HTMLImageElement | null>(null);
  const previewRequestIdRef = useRef(0);
  const [signatures, setSignatures] = useState<UserSignatureInfo[]>([]);
  const [previewUrls, setPreviewUrls] = useState<Record<number, string>>({});
  const [legacyPreviewUrl, setLegacyPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [uploadStep, setUploadStep] = useState<1 | 2>(1);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [originalObjectUrl, setOriginalObjectUrl] = useState<string | null>(null);
  const [croppedFile, setCroppedFile] = useState<File | null>(null);
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [processedPreview, setProcessedPreview] =
    useState<ProcessedSignaturePreview | null>(null);
  const [signatureName, setSignatureName] = useState("");
  const [approved, setApproved] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const selectedId = isUserSignatureReference(currentValue)
    ? Number(currentValue.id)
    : null;
  const legacyId = getLegacySignatureId(currentValue);
  const selectedSignature = signatures.find((signature) => signature.id === selectedId);

  const processedDataUrl = useMemo(
    () =>
      processedPreview
        ? `data:${processedPreview.processedMimeType};base64,${processedPreview.processedImageBase64}`
        : null,
    [processedPreview]
  );

  const loadLibrary = useCallback(async () => {
    setLoading(true);
    setFieldError(null);
    try {
      const items = await listUserSignatures();
      setSignatures(items);
      const previews = await Promise.all(
        items.map(async (item) => {
          try {
            return [item.id, await getUserSignaturePreviewUrl(item.id)] as const;
          } catch {
            return [item.id, ""] as const;
          }
        })
      );
      setPreviewUrls(Object.fromEntries(previews.filter(([, url]) => Boolean(url))));
    } catch (error) {
      setFieldError(errorMessage(error, "Could not load your saved signatures."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  useEffect(() => {
    if (!legacyId) {
      setLegacyPreviewUrl(null);
      return () => {};
    }
    let cancelled = false;
    getSignaturePreviewUrl(applicationId, legacyId)
      .then((url) => {
        if (!cancelled) setLegacyPreviewUrl(url);
      })
      .catch(() => {
        if (!cancelled) setLegacyPreviewUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [applicationId, legacyId]);

  const clearCandidate = useCallback(() => {
    previewRequestIdRef.current += 1;
    if (originalObjectUrl) URL.revokeObjectURL(originalObjectUrl);
    if (croppedPreviewUrl) URL.revokeObjectURL(croppedPreviewUrl);
    cropImageRef.current = null;
    setUploadStep(1);
    setSourceFile(null);
    setOriginalObjectUrl(null);
    setCroppedFile(null);
    setCroppedPreviewUrl(null);
    setCrop(undefined);
    setCompletedCrop(null);
    setProcessedPreview(null);
    setSignatureName("");
    setApproved(false);
    setModalError(null);
    setProcessing(false);
    setIsDraggingFile(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [croppedPreviewUrl, originalObjectUrl]);

  const closeModal = useCallback(() => {
    if (processing || saving) return;
    clearCandidate();
    setModalOpen(false);
  }, [clearCandidate, processing, saving]);

  useEffect(
    () => () => {
      if (originalObjectUrl) URL.revokeObjectURL(originalObjectUrl);
    },
    [originalObjectUrl]
  );

  useEffect(
    () => () => {
      if (croppedPreviewUrl) URL.revokeObjectURL(croppedPreviewUrl);
    },
    [croppedPreviewUrl]
  );

  useEffect(() => {
    if (!modalOpen) return () => {};
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeModal, modalOpen]);

  const selectSignature = useCallback(
    (id: number) => {
      setFormData((previous) => ({
        ...previous,
        [inputName]: { type: "user_signature", id } satisfies UserSignatureReference,
      }));
      setFieldError(null);
    },
    [inputName, setFormData]
  );

  const applyCropSelection = useCallback((image: HTMLImageElement, nextCrop: Crop) => {
    setCrop(nextCrop);
    setCompletedCrop(convertToPixelCrop(nextCrop, image.width, image.height));
  }, []);

  const handleImageLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      const image = event.currentTarget;
      cropImageRef.current = image;
      applyCropSelection(image, createDefaultCrop(image.width, image.height));
      setModalError(null);
    },
    [applyCropSelection]
  );

  const handleCropChange = useCallback((nextCrop: Crop) => {
    setCrop(nextCrop);
    setModalError(null);
  }, []);

  const handleCropComplete = useCallback((nextCrop: PixelCrop) => {
    setCompletedCrop(nextCrop);
  }, []);

  const resetCrop = useCallback(() => {
    const image = cropImageRef.current;
    if (!image) return;
    applyCropSelection(image, createDefaultCrop(image.width, image.height));
    setModalError(null);
  }, [applyCropSelection]);

  const selectCandidateFile = useCallback(
    (file: File | null | undefined) => {
      if (!file) return;

      const mime = (file.type || "").toLowerCase();
      const name = file.name.toLowerCase();
      const isSupported =
        mime === "image/png" ||
        mime === "image/jpeg" ||
        name.endsWith(".png") ||
        name.endsWith(".jpg") ||
        name.endsWith(".jpeg");
      if (!isSupported) {
        setModalError("Please drop a PNG or JPEG signature scan.");
        return;
      }

      previewRequestIdRef.current += 1;
      if (originalObjectUrl) URL.revokeObjectURL(originalObjectUrl);
      if (croppedPreviewUrl) URL.revokeObjectURL(croppedPreviewUrl);
      cropImageRef.current = null;
      setUploadStep(1);
      setIsDraggingFile(false);
      setSourceFile(file);
      setOriginalObjectUrl(URL.createObjectURL(file));
      setCroppedFile(null);
      setCroppedPreviewUrl(null);
      setCrop(undefined);
      setCompletedCrop(null);
      setProcessedPreview(null);
      setApproved(false);
      setModalError(null);
      setProcessing(false);
      setSignatureName(file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim());
    },
    [croppedPreviewUrl, originalObjectUrl]
  );

  const handleCandidateFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      selectCandidateFile(event.target.files?.[0]);
      if (event.target) event.target.value = "";
    },
    [selectCandidateFile]
  );

  const handleDropZoneDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (processing || saving) return;
      event.dataTransfer.dropEffect = "copy";
      setIsDraggingFile(true);
    },
    [processing, saving]
  );

  const handleDropZoneDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) return;
    setIsDraggingFile(false);
  }, []);

  const handleDropZoneDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDraggingFile(false);
      if (processing || saving) return;
      const file = event.dataTransfer.files?.[0];
      selectCandidateFile(file);
    },
    [processing, saving, selectCandidateFile]
  );

  const goBackToCrop = useCallback(() => {
    if (processing || saving) return;
    previewRequestIdRef.current += 1;
    if (croppedPreviewUrl) URL.revokeObjectURL(croppedPreviewUrl);
    setUploadStep(1);
    setCroppedFile(null);
    setCroppedPreviewUrl(null);
    setProcessedPreview(null);
    setApproved(false);
    setModalError(null);
    setProcessing(false);
  }, [croppedPreviewUrl, processing, saving]);

  const generateTransparentSignature = useCallback(async () => {
    const image = cropImageRef.current;
    if (!sourceFile || !image?.complete || !completedCrop?.width || !completedCrop?.height) {
      setModalError("Crop the signature tightly before continuing.");
      return;
    }

    const requestId = ++previewRequestIdRef.current;
    setApproved(false);
    setProcessedPreview(null);
    setProcessing(true);
    setModalError(null);

    try {
      const nextCropped = await cropImageToFile(image, completedCrop, {
        mimeType: sourceFile.type,
        fileName: sourceFile.name,
      });
      if (requestId !== previewRequestIdRef.current) return;
      const preview = await processUserSignaturePreview(nextCropped);
      if (requestId !== previewRequestIdRef.current) return;
      if (croppedPreviewUrl) URL.revokeObjectURL(croppedPreviewUrl);
      setCroppedFile(nextCropped);
      setCroppedPreviewUrl(URL.createObjectURL(nextCropped));
      setProcessedPreview(preview);
      setUploadStep(2);
    } catch (error) {
      if (requestId !== previewRequestIdRef.current) return;
      setCroppedFile(null);
      setProcessedPreview(null);
      setModalError(
        errorMessage(error, "We could not process this crop. Try framing the ink more tightly.")
      );
    } finally {
      if (requestId === previewRequestIdRef.current) {
        setProcessing(false);
      }
    }
  }, [completedCrop, croppedPreviewUrl, sourceFile]);

  const saveCandidate = useCallback(async () => {
    const normalizedName = signatureName.trim().replace(/\s+/g, " ");
    if (!croppedFile || !processedPreview) {
      setModalError("Generate the transparent signature, then approve it.");
      return;
    }
    if (!normalizedName) {
      setModalError("Give this signature a name.");
      return;
    }
    if (
      signatures.some(
        (signature) => signature.name.trim().toLowerCase() === normalizedName.toLowerCase()
      )
    ) {
      setModalError("You already have a signature with this name.");
      return;
    }
    if (!approved) {
      setModalError("Confirm that the processed signature looks correct.");
      return;
    }

    setSaving(true);
    setModalError(null);
    try {
      const created = await createUserSignature(croppedFile, normalizedName);
      setSignatures((previous) => [created, ...previous]);
      setPreviewUrls((previous) => ({
        ...previous,
        [created.id]: processedDataUrl || "",
      }));
      selectSignature(created.id);
      clearCandidate();
      setModalOpen(false);
    } catch (error) {
      setModalError(errorMessage(error, "Could not save this signature."));
    } finally {
      setSaving(false);
    }
  }, [
    approved,
    clearCandidate,
    croppedFile,
    processedDataUrl,
    processedPreview,
    selectSignature,
    signatureName,
    signatures,
  ]);

  const handleRename = useCallback(async (signature: UserSignatureInfo) => {
    const nextName = window.prompt("Rename signature", signature.name)?.trim();
    if (!nextName || nextName === signature.name) return;
    setBusyId(signature.id);
    setFieldError(null);
    try {
      const renamed = await renameUserSignature(signature.id, nextName);
      setSignatures((previous) =>
        previous.map((item) => (item.id === signature.id ? renamed : item))
      );
    } catch (error) {
      setFieldError(errorMessage(error, "Could not rename this signature."));
    } finally {
      setBusyId(null);
    }
  }, []);

  const handleDelete = useCallback(
    async (signature: UserSignatureInfo) => {
      if (selectedId === signature.id) {
        setFieldError("Choose a different signature for this question before deleting it.");
        return;
      }
      if (!window.confirm(`Delete “${signature.name}”? This cannot be undone.`)) return;
      setBusyId(signature.id);
      setFieldError(null);
      try {
        await deleteUserSignature(signature.id);
        setSignatures((previous) => previous.filter((item) => item.id !== signature.id));
        setPreviewUrls((previous) => {
          const next = { ...previous };
          delete next[signature.id];
          return next;
        });
      } catch (error) {
        setFieldError(errorMessage(error, "Could not delete this signature."));
      } finally {
        setBusyId(null);
      }
    },
    [selectedId]
  );

  const selectedPreviewUrl = selectedId ? previewUrls[selectedId] : legacyPreviewUrl;
  const hasSelection = Boolean(selectedId || legacyId);
  const canApprove = Boolean(croppedFile && processedPreview && !processing);

  if (readOnly) {
    return (
      <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
          Selected signature
        </p>
        {hasSelection ? (
          <div className="flex items-center gap-4">
            {selectedPreviewUrl ? (
              <img
                src={selectedPreviewUrl}
                alt={selectedSignature?.name || "Selected signature"}
                className="h-16 max-w-[220px] rounded-lg border border-[#E5E7EB] object-contain p-2"
                style={checkerboardStyle}
              />
            ) : null}
            <div>
              <p className="text-sm font-semibold text-[#1B365D]">
                {selectedSignature?.name || "Legacy application signature"}
              </p>
              <p className="text-xs text-[#6B7280]">Saved with this form answer</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#6B7280]">No signature selected.</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className={`rounded-xl border bg-white p-4 ${
          hasSelection ? "border-[#86C89A]" : "border-[#E5E7EB]"
        }`}
      >
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span
                className={`grid h-7 w-7 place-items-center rounded-full ${
                  hasSelection ? "bg-[#E7F6EC] text-[#1C7A43]" : "bg-[#F3F4F6] text-[#6B7280]"
                }`}
              >
                {hasSelection ? <FiCheck aria-hidden="true" /> : "✎"}
              </span>
              <p className="text-sm font-semibold text-[#1B365D]">
                {hasSelection ? "Signature selected" : "Choose your signature"}
              </p>
            </div>
            <p className="mt-1 text-xs text-[#6B7280]">
              Saved signatures are private and can be reused in future applications.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-full bg-[#F4A261] px-4 py-2 text-sm font-semibold text-[#1B365D] transition hover:bg-[#E9954F]"
          >
            <FiPlus aria-hidden="true" />
            Upload new
          </button>
        </div>

        {fieldError ? (
          <div role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
            {fieldError}
          </div>
        ) : null}

        {legacyId ? (
          <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-[#F0D4A8] bg-[#FFF8EC] p-3">
            <div className="flex items-center gap-3">
              {legacyPreviewUrl ? (
                <img
                  src={legacyPreviewUrl}
                  alt="Legacy application signature"
                  className="h-12 w-28 rounded border border-[#E5E7EB] object-contain bg-white p-1"
                />
              ) : null}
              <div>
                <p className="text-sm font-semibold text-[#1B365D]">Legacy application signature</p>
                <p className="text-xs text-[#6B7280]">Select a library signature to replace it.</p>
              </div>
            </div>
            <span className="rounded-full bg-[#FFF0D5] px-2 py-1 text-[11px] font-semibold text-[#9A6212]">
              Current
            </span>
          </div>
        ) : null}

        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {[0, 1].map((item) => (
              <div
                key={item}
                className="h-24 animate-pulse rounded-xl border border-[#E5E7EB] bg-[#F8FAFC]"
              />
            ))}
          </div>
        ) : signatures.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {signatures.map((signature) => {
              const isSelected = selectedId === signature.id;
              const isBusy = busyId === signature.id;
              return (
                <div
                  key={signature.id}
                  className={`rounded-xl border p-3 transition ${
                    isSelected
                      ? "border-[#1C7A43] bg-[#F3FBF5] shadow-sm"
                      : "border-[#E5E7EB] hover:border-[#F4A261]"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => selectSignature(signature.id)}
                    disabled={isBusy}
                    className="flex w-full items-center gap-3 text-left disabled:opacity-60"
                    aria-pressed={isSelected}
                  >
                    <div
                      className="grid h-14 w-28 flex-none place-items-center overflow-hidden rounded-lg border border-[#E5E7EB] p-1"
                      style={checkerboardStyle}
                    >
                      {previewUrls[signature.id] ? (
                        <img
                          src={previewUrls[signature.id]}
                          alt=""
                          className="max-h-12 max-w-[100px] object-contain"
                        />
                      ) : (
                        <span className="text-[11px] text-[#9CA3AF]">Preview unavailable</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-[#1B365D]">
                        {signature.name}
                      </p>
                      <p className="text-xs text-[#6B7280]">
                        {signature.processedWidth} × {signature.processedHeight}px
                      </p>
                    </div>
                    <span
                      className={`grid h-5 w-5 place-items-center rounded-full border ${
                        isSelected
                          ? "border-[#1C7A43] bg-[#1C7A43] text-white"
                          : "border-[#CBD5E1] bg-white"
                      }`}
                    >
                      {isSelected ? <FiCheck className="text-xs" aria-hidden="true" /> : null}
                    </span>
                  </button>
                  <div className="mt-2 flex justify-end gap-1 border-t border-[#E8ECEF] pt-2">
                    {previewUrls[signature.id] ? (
                      <button
                        type="button"
                        title="Open signature preview"
                        onClick={() =>
                          window.open(
                            previewUrls[signature.id],
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                        className="grid h-8 w-8 place-items-center rounded-lg text-[#1B365D] hover:bg-white"
                      >
                        <FiEye aria-hidden="true" />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      title="Rename signature"
                      onClick={() => handleRename(signature)}
                      disabled={isBusy}
                      className="grid h-8 w-8 place-items-center rounded-lg text-[#1B365D] hover:bg-white disabled:opacity-50"
                    >
                      <FiEdit2 aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      title="Delete signature"
                      onClick={() => handleDelete(signature)}
                      disabled={isBusy}
                      className="grid h-8 w-8 place-items-center rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-50"
                    >
                      <FiTrash2 aria-hidden="true" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#C8CED8] bg-[#F8FAFC] px-4 py-8 text-sm font-semibold text-[#1B365D] hover:border-[#F4A261] hover:bg-[#FFF9F2]"
          >
            <FiUpload aria-hidden="true" />
            Upload your first scanned signature
          </button>
        )}
      </div>

      {modalOpen ? (
        <div
          className="fixed inset-0 z-[1200] flex items-center justify-center bg-[#0B172A]/65 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="signature-upload-title"
        >
          <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex flex-none items-start justify-between border-b border-[#E5E7EB] px-5 py-4 sm:px-6">
              <div>
                <h3 id="signature-upload-title" className="text-lg font-bold text-[#1B365D]">
                  Add a reusable signature
                </h3>
                <p className="mt-1 text-xs text-[#6B7280]">
                  {uploadStep === 1
                    ? "Step 1 of 2 — Choose a scan and crop it to just the ink."
                    : `Step 2 of 2 — Review the transparent result for “${
                        question.label || question.questionText
                      }”.`}
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={processing || saving}
                title="Close"
                className="grid h-9 w-9 place-items-center rounded-full text-[#64748B] hover:bg-[#F1F5F9] disabled:opacity-50"
              >
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-5 sm:p-6">
              <div className="flex flex-none gap-2">
                <div
                  className={`flex-1 rounded-full px-3 py-1.5 text-center text-xs font-bold ${
                    uploadStep === 1
                      ? "bg-[#1B365D] text-white"
                      : "bg-[#E7F6EC] text-[#1C7A43]"
                  }`}
                >
                  1 · Crop
                </div>
                <div
                  className={`flex-1 rounded-full px-3 py-1.5 text-center text-xs font-bold ${
                    uploadStep === 2
                      ? "bg-[#1B365D] text-white"
                      : "bg-[#F1F5F9] text-[#64748B]"
                  }`}
                >
                  2 · Transparent result
                </div>
              </div>

              {modalError ? (
                <div role="alert" className="flex-none rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {modalError}
                </div>
              ) : null}

              {uploadStep === 1 ? (
                <div className="flex min-h-0 flex-1 flex-col gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,.jpg,.jpeg"
                    onChange={handleCandidateFile}
                    disabled={processing || saving}
                    className="hidden"
                  />

                  {sourceFile && originalObjectUrl ? (
                    <>
                      <div
                        onDragEnter={handleDropZoneDragOver}
                        onDragOver={handleDropZoneDragOver}
                        onDragLeave={handleDropZoneDragLeave}
                        onDrop={handleDropZoneDrop}
                        className={`flex flex-none items-center justify-between gap-3 rounded-xl border px-3 py-2 transition ${
                          isDraggingFile
                            ? "border-[#F4A261] bg-[#FFF9F2] ring-2 ring-[#F4A261]/30"
                            : "border-[#E5E7EB] bg-[#F8FAFC]"
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-[#1B365D]">
                            {sourceFile.name}
                          </p>
                          <p className="text-xs text-[#6B7280]">
                            {isDraggingFile
                              ? "Drop to replace this scan"
                              : "Crop below, or replace the image"}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={processing || saving}
                          className="inline-flex flex-none items-center gap-2 rounded-full bg-[#1B365D] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#15294A] disabled:opacity-50"
                        >
                          <FiUpload aria-hidden="true" />
                          Replace
                        </button>
                      </div>

                      <div className="flex min-h-0 flex-1 flex-col">
                        <div className="mb-2 flex flex-none items-center justify-between gap-2">
                          <p className="text-xs font-bold uppercase tracking-wide text-[#6B7280]">
                            Crop to signature
                          </p>
                          <button
                            type="button"
                            onClick={resetCrop}
                            disabled={processing || saving}
                            className="text-xs font-semibold text-[#1B365D] hover:underline disabled:opacity-50"
                          >
                            Reset crop
                          </button>
                        </div>
                        <div
                          className="signature-crop flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-2"
                          style={
                            {
                              ["--rc-border-color" as string]: "#F4A261",
                              ["--rc-focus-color" as string]: "#F4A261",
                              ["--rc-drag-handle-bg-colour" as string]: "#F4A261",
                              ["--rc-drag-handle-border-colour" as string]: "#1B365D",
                            } as React.CSSProperties
                          }
                        >
                          <ReactCrop
                            crop={crop}
                            onChange={handleCropChange}
                            onComplete={handleCropComplete}
                            minWidth={24}
                            minHeight={16}
                            keepSelection
                            className="max-h-[40vh] max-w-full"
                          >
                            <img
                              ref={cropImageRef}
                              src={originalObjectUrl}
                              alt="Original scanned signature"
                              onLoad={handleImageLoad}
                              className="max-h-[40vh] max-w-full object-contain"
                            />
                          </ReactCrop>
                        </div>
                        <p className="mt-2 flex-none text-xs text-[#6B7280]">
                          Drag the box and use the handles so only the ink remains. No processing
                          runs until the next step.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div
                      role="button"
                      tabIndex={processing || saving ? -1 : 0}
                      aria-label="Drop a PNG or JPEG signature scan, or press to choose a file"
                      onClick={() => {
                        if (processing || saving) return;
                        fileInputRef.current?.click();
                      }}
                      onKeyDown={(event) => {
                        if (processing || saving) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          fileInputRef.current?.click();
                        }
                      }}
                      onDragEnter={handleDropZoneDragOver}
                      onDragOver={handleDropZoneDragOver}
                      onDragLeave={handleDropZoneDragLeave}
                      onDrop={handleDropZoneDrop}
                      className={`flex min-h-[14rem] flex-1 flex-col items-center justify-center rounded-xl border border-dashed p-6 text-center transition ${
                        isDraggingFile
                          ? "border-[#F4A261] bg-[#FFF9F2] ring-2 ring-[#F4A261]/30"
                          : "border-[#C8CED8] bg-[#F8FAFC] hover:border-[#F4A261] hover:bg-[#FFF9F2]"
                      } ${processing || saving ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                    >
                      <div className="mb-3 grid h-12 w-12 place-items-center rounded-full bg-white text-[#1B365D] shadow-sm">
                        <FiUpload aria-hidden="true" className="text-xl" />
                      </div>
                      <p className="text-sm font-semibold text-[#1B365D]">
                        {isDraggingFile
                          ? "Drop your signature scan here"
                          : "Drag and drop a signature scan here"}
                      </p>
                      <p className="mt-1 text-xs text-[#6B7280]">
                        PNG or JPEG · blue or black ink on plain white paper
                      </p>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                        disabled={processing || saving}
                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#1B365D] px-4 py-2 text-sm font-semibold text-white hover:bg-[#15294A] disabled:opacity-50"
                      >
                        <FiUpload aria-hidden="true" />
                        Choose PNG or JPEG
                      </button>
                    </div>
                  )}

                  {processing ? (
                    <div className="flex-none rounded-xl border border-[#C5D9F5] bg-[#EAF3FF] p-4 text-sm text-[#1D4F91]">
                      Generating your transparent signature…
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[#6B7280]">
                        Your crop
                      </p>
                      <div className="grid h-40 place-items-center overflow-hidden rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-3 sm:h-48">
                        {croppedPreviewUrl ? (
                          <img
                            src={croppedPreviewUrl}
                            alt="Cropped signature"
                            className="max-h-full max-w-full object-contain"
                          />
                        ) : null}
                      </div>
                    </div>
                    <div>
                      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[#1C7A43]">
                        Transparent result
                      </p>
                      <div
                        className="grid h-40 place-items-center overflow-hidden rounded-xl border-2 border-[#86C89A] p-3 sm:h-48"
                        style={checkerboardStyle}
                      >
                        {processedDataUrl ? (
                          <img
                            src={processedDataUrl}
                            alt="Processed transparent signature"
                            className="max-h-full max-w-full object-contain"
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="signature-name"
                      className="mb-1 block text-sm font-semibold text-[#1B365D]"
                    >
                      Signature name
                    </label>
                    <input
                      id="signature-name"
                      value={signatureName}
                      onChange={(event) => setSignatureName(event.target.value)}
                      maxLength={100}
                      placeholder="For example: Full signature"
                      disabled={saving}
                      className="w-full rounded-xl border border-[#CBD5E1] px-3 py-2.5 text-sm text-[#1F2933] outline-none transition focus:border-[#F4A261] focus:ring-2 focus:ring-[#F4A261]/20"
                    />
                  </div>

                  <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-3">
                    <input
                      type="checkbox"
                      checked={approved}
                      onChange={(event) => setApproved(event.target.checked)}
                      disabled={saving || !canApprove}
                      className="mt-0.5 h-4 w-4 accent-[#1C7A43]"
                    />
                    <span className="text-sm text-[#334155]">
                      <strong className="text-[#1B365D]">
                        This processed signature looks correct.
                      </strong>{" "}
                      Save it exactly as shown for reuse in my forms.
                    </span>
                  </label>
                </div>
              )}
            </div>

            <div className="flex flex-none flex-wrap justify-end gap-2 border-t border-[#E5E7EB] px-5 py-4 sm:px-6">
              {uploadStep === 2 ? (
                <button
                  type="button"
                  onClick={goBackToCrop}
                  disabled={processing || saving}
                  className="mr-auto rounded-full border border-[#CBD5E1] px-4 py-2 text-sm font-semibold text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50"
                >
                  Back to crop
                </button>
              ) : null}
              <button
                type="button"
                onClick={closeModal}
                disabled={processing || saving}
                className="rounded-full border border-[#CBD5E1] px-4 py-2 text-sm font-semibold text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50"
              >
                Cancel
              </button>
              {uploadStep === 1 ? (
                <button
                  type="button"
                  onClick={() => void generateTransparentSignature()}
                  disabled={
                    processing ||
                    saving ||
                    !sourceFile ||
                    !completedCrop?.width ||
                    !completedCrop?.height
                  }
                  className="inline-flex items-center gap-2 rounded-full bg-[#F4A261] px-5 py-2 text-sm font-bold text-[#1B365D] hover:bg-[#E9954F] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {processing ? "Generating…" : "Generate transparent signature"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={saveCandidate}
                  disabled={!canApprove || !approved || saving}
                  className="inline-flex items-center gap-2 rounded-full bg-[#F4A261] px-5 py-2 text-sm font-bold text-[#1B365D] hover:bg-[#E9954F] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FiCheck aria-hidden="true" />
                  {saving ? "Saving…" : "Approve and save"}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default SignatureFieldRenderer;
